import React from 'react';
import * as dm from '../../data-models';
import dayjs from 'dayjs';
import { log } from '../../utils/log';

export const fullDateFormat = 'MMM D, YYYY';
export const baseWeekCount = 12;
export const timelineViewportHeight = 520;
export const timelineGutterPx = 35;
export const timelineInnerGutterPx = timelineGutterPx * 2;
export const timelineRowSpacingPx = 75;
export const mobileWeekLabelHeightPx = 12;
export const mobileWeekInnerGapPx = 8;
export const mobileWeekConnectorHeightPx = 20;
export const mobileWeekOuterGapPx = 8;
export const mobileLargestBarMinHeightPx = 400;
export const mobileLaneWidthPx = 52;
export const mobileBarMinHeightPx = 24;

export type LinearBar = {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  styleType: 'job' | 'paid' | 'partial' | 'wait';
  relatedToId?: string;
};

export type MobileLinearBar = LinearBar & {
  durationDays: number;
  letter: string;
};

export type MobilePositionedBar = MobileLinearBar & {
  lane: number;
};

export const barStyleSortOrder: Record<LinearBar['styleType'], number> = {
  wait: 0,
  paid: 1,
  partial: 2,
  job: 3,
};

export const timelineLegendDefinitions: Array<{
  styleType: LinearBar['styleType'];
  label: string;
}> = [
  { styleType: 'job', label: 'Job Protection' },
  { styleType: 'paid', label: 'Paid Leave' },
  { styleType: 'partial', label: 'Partial Earnings' },
  { styleType: 'wait', label: 'Waiting Period' },
];

export const mobileStyleOrder: LinearBar['styleType'][] = [
  'wait',
  'paid',
  'partial',
  'job',
];

export interface IHandleLeavePlanInit {
  draftClaim?: dm.DraftClaim;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setRawElements: React.Dispatch<React.SetStateAction<any>>;
  setPeriods: React.Dispatch<React.SetStateAction<any>>;
  setBenefits: React.Dispatch<React.SetStateAction<any>>;
}

export const toIsoDate = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const normalized = value.replaceAll('/', '-');
  const parsed = dayjs(normalized);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

export const getEmptyTimelineData = () => ({
  rawElements: [] as any[],
  periods: [] as any[],
  benefits: {
    jobProtectionBenefits: [] as any[],
    incomeProtectionBenefits: [] as any[],
  },
});

export const hasTimelineData = (timelineData: {
  rawElements: any[];
  periods: any[];
  benefits: {
    jobProtectionBenefits: any[];
    incomeProtectionBenefits: any[];
  };
}) =>
  timelineData.rawElements.length > 0 ||
  timelineData.periods.length > 0 ||
  timelineData.benefits.jobProtectionBenefits.length > 0 ||
  timelineData.benefits.incomeProtectionBenefits.length > 0;

export const mapDraftClaimToTimeline = (draftClaim?: dm.DraftClaim) => {
  if (!draftClaim) {
    return getEmptyTimelineData();
  }

  const rawElements: any[] = [];
  const jobProtectionBenefits: any[] = [];
  const incomeProtectionBenefits: any[] = [];

  const leaveStartDate = toIsoDate(draftClaim.startDate);
  const leaveEndDate = toIsoDate(draftClaim.endDate);

  (draftClaim.milestones ?? []).forEach((milestone) => {
    const milestoneDate = toIsoDate(milestone?.date);
    if (!milestoneDate) {
      return;
    }

    rawElements.push({
      startDate: milestoneDate,
      endDate: milestoneDate,
      type: 'milestone',
      legendKey: 'milestone',
      meta: {
        name: milestone?.name ?? '',
      },
    });
  });

  (draftClaim.paidBenefits ?? []).forEach((benefit) => {
    const startDate = toIsoDate(benefit?.startDate);
    const endDate = toIsoDate(benefit?.endDate);

    if (!startDate || !endDate) {
      return;
    }

    const waitingPeriodStartDate = toIsoDate(benefit?.waitingPeriodStartDate);
    const waitingPeriodEndDate = toIsoDate(benefit?.waitingPeriodEndDate);

    const entry = {
      startDate,
      endDate,
      type: 'paidBenefits',
      legendKey: 'paidBenefits',
      meta: {
        name: benefit?.name ?? 'Paid Benefits',
        waitingPeriodStartDate,
        waitingPeriodEndDate,
      },
    };

    rawElements.push(entry);
    incomeProtectionBenefits.push(entry);
  });

  (draftClaim.potentialPaidBenefits ?? []).forEach((benefit) => {
    const startDate = toIsoDate(benefit?.startDate);
    const endDate = toIsoDate(benefit?.endDate);

    if (!startDate || !endDate) {
      return;
    }

    const entry = {
      startDate,
      endDate,
      type: 'potentialPaidBenefits',
      legendKey: 'potentialPaidBenefits',
      meta: {
        name: 'Partial Paid',
      },
    };

    rawElements.push(entry);
    incomeProtectionBenefits.push(entry);
  });

  (draftClaim.jobProtectedBenefits ?? []).forEach((benefit) => {
    const startDate = toIsoDate(benefit?.startDate);
    const endDate = toIsoDate(benefit?.endDate);

    if (!startDate || !endDate) {
      return;
    }

    const waitingBenefit = benefit as dm.JobProtectedBenefit & {
      waitingPeriodStartDate?: string;
      waitingPeriodEndDate?: string;
    };
    const waitingPeriodStartDate = toIsoDate(
      waitingBenefit?.waitingPeriodStartDate,
    );
    const waitingPeriodEndDate = toIsoDate(
      waitingBenefit?.waitingPeriodEndDate,
    );

    const entry = {
      startDate,
      endDate,
      type: 'jobProtection',
      legendKey: 'potentialjobprotection',
      meta: {
        name: benefit?.name ?? 'Job Protection',
        waitingPeriodStartDate,
        waitingPeriodEndDate,
      },
    };

    rawElements.push(entry);
    jobProtectionBenefits.push(entry);
  });

  const periods =
    leaveStartDate && leaveEndDate
      ? [
          {
            startDate: leaveStartDate,
            endDate: leaveEndDate,
          },
        ]
      : [];

  return {
    rawElements,
    periods,
    benefits: {
      jobProtectionBenefits,
      incomeProtectionBenefits,
    },
  };
};

export const getSummaryMetrics = (periods: any[]) => {
  if (!periods || periods.length === 0) {
    return {
      startDate: undefined,
      startDateValue: undefined,
      endDate: undefined,
      endDateValue: undefined,
      leaveLength: undefined,
    };
  }

  let startDate = dayjs(periods[0].startDate);
  let endDate = dayjs(periods[0].endDate);

  periods.forEach((period) => {
    const periodStart = dayjs(period.startDate);
    const periodEnd = dayjs(period.endDate);
    if (periodStart.isBefore(startDate)) {
      startDate = periodStart;
    }
    if (periodEnd.isAfter(endDate)) {
      endDate = periodEnd;
    }
  });

  const totalDays = endDate.diff(startDate, 'day') + 1;
  const weeks = Math.max(1, Math.ceil(totalDays / 7));

  return {
    startDate: startDate.format(fullDateFormat),
    startDateValue: startDate.toISOString(),
    endDate: endDate.format(fullDateFormat),
    endDateValue: endDate.toISOString(),
    leaveLength: `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`,
  };
};

export const barsByDateSort = (a: LinearBar, b: LinearBar) => {
  const startDiff = dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf();
  if (startDiff !== 0) {
    return startDiff;
  }

  const endDiff = dayjs(a.endDate).valueOf() - dayjs(b.endDate).valueOf();
  if (endDiff !== 0) {
    return endDiff;
  }

  return a.id.localeCompare(b.id);
};

export const positionMobileTimelineBars = (bars: MobileLinearBar[]) => {
  const relatedWaitBars = bars.filter(
    (bar) => bar.styleType === 'wait' && !!bar.relatedToId,
  );
  const nonRelatedWaitBars = bars.filter(
    (bar) => !(bar.styleType === 'wait' && !!bar.relatedToId),
  );
  const hasRelatedWaitByParentId = new Set(
    relatedWaitBars
      .map((waitBar) => waitBar.relatedToId)
      .filter((id): id is string => !!id),
  );

  const laneEndByStyle: Record<LinearBar['styleType'], dayjs.Dayjs[]> = {
    wait: [],
    paid: [],
    partial: [],
    job: [],
  };
  const laneCountByStyle: Record<LinearBar['styleType'], number> = {
    wait: 0,
    paid: 0,
    partial: 0,
    job: 0,
  };
  const relatedWaitSubLanesByStyle: Record<
    LinearBar['styleType'],
    Set<number>
  > = {
    wait: new Set<number>(),
    paid: new Set<number>(),
    partial: new Set<number>(),
    job: new Set<number>(),
  };

  const withSubLanes = nonRelatedWaitBars.map((bar) => {
    const laneEnds = laneEndByStyle[bar.styleType];
    const barStart = dayjs(bar.startDate);
    const barEnd = dayjs(bar.endDate);
    let subLane = laneEnds.findIndex((laneEnd) =>
      barStart.isAfter(laneEnd, 'day'),
    );

    if (subLane === -1) {
      subLane = laneEnds.length;
      laneEnds.push(barEnd);
    } else {
      laneEnds[subLane] = barEnd;
    }

    laneCountByStyle[bar.styleType] = Math.max(
      laneCountByStyle[bar.styleType],
      subLane + 1,
    );

    if (hasRelatedWaitByParentId.has(bar.id)) {
      relatedWaitSubLanesByStyle[bar.styleType].add(subLane);
    }

    return {
      bar,
      subLane,
    };
  });

  const laneOffsetByStyle: Record<LinearBar['styleType'], number> = {
    wait: 0,
    paid: 0,
    partial: 0,
    job: 0,
  };
  const subLaneOffsetByStyle: Record<LinearBar['styleType'], number[]> = {
    wait: [],
    paid: [],
    partial: [],
    job: [],
  };
  let laneOffset = 0;
  mobileStyleOrder.forEach((styleType) => {
    laneOffsetByStyle[styleType] = laneOffset;

    const subLaneOffsets: number[] = [];
    let subLaneOffset = 0;
    for (let subLane = 0; subLane < laneCountByStyle[styleType]; subLane += 1) {
      subLaneOffsets[subLane] = subLaneOffset;
      const slotWidth = relatedWaitSubLanesByStyle[styleType].has(subLane)
        ? 2
        : 1;
      subLaneOffset += slotWidth;
    }
    subLaneOffsetByStyle[styleType] = subLaneOffsets;
    laneOffset += subLaneOffset;
  });

  const positionedNonWaitBars: MobilePositionedBar[] = withSubLanes.map(
    ({ bar, subLane }) => ({
      ...bar,
      lane:
        laneOffsetByStyle[bar.styleType] +
        subLaneOffsetByStyle[bar.styleType][subLane] +
        (hasRelatedWaitByParentId.has(bar.id) ? 1 : 0),
    }),
  );

  const laneByBarId = new Map<string, number>(
    positionedNonWaitBars.map((bar) => [bar.id, bar.lane]),
  );
  const positionedRelatedWaitBars: MobilePositionedBar[] = relatedWaitBars
    .map((bar) => {
      const relatedLane = bar.relatedToId
        ? laneByBarId.get(bar.relatedToId)
        : undefined;
      if (relatedLane === undefined) {
        return null;
      }

      return {
        ...bar,
        lane: Math.max(0, relatedLane - 1),
      };
    })
    .filter((bar): bar is MobilePositionedBar => !!bar);

  const positionedBars = [
    ...positionedNonWaitBars,
    ...positionedRelatedWaitBars,
  ].sort((a, b) => {
    const styleDiff =
      barStyleSortOrder[a.styleType] - barStyleSortOrder[b.styleType];
    if (styleDiff !== 0) {
      return styleDiff;
    }

    return barsByDateSort(a, b);
  });

  const laneCount = Math.max(1, ...positionedBars.map((bar) => bar.lane + 1));

  return {
    positionedBars,
    laneCount,
  };
};

export const toText = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

export const normalizeKind = (value: unknown) => toText(value).toLowerCase();

export const hasValue = (value: unknown) => toText(value).trim().length > 0;

export const toStyleType = (element: any): LinearBar['styleType'] | null => {
  const legendKey = normalizeKind(element?.legendKey);
  const type = normalizeKind(element?.type);

  const jobKeys = [
    'potentialjobprotection',
    'approvedjpcontinuous',
    'pendingjpcontinuous',
    'deniedjpcontinuous',
  ];
  if (jobKeys.includes(legendKey) || type === 'jobprotection') {
    return 'job';
  }

  const paidKeys = [
    'paidbenefits',
    'approvedplcontinuous',
    'pendingplcontinuous',
    'deniedplcontinuous',
  ];
  if (paidKeys.includes(legendKey) || type === 'paidbenefits') {
    const hasWaiting =
      hasValue(element?.meta?.waitingPeriodStartDate) &&
      hasValue(element?.meta?.waitingPeriodEndDate);
    return hasWaiting ? 'partial' : 'paid';
  }

  if (
    legendKey === 'potentialpaidbenefits' ||
    type === 'potentialpaidbenefits'
  ) {
    return 'partial';
  }

  if (legendKey === 'waitingperiod' || type === 'waitingperiod') {
    return 'wait';
  }

  return null;
};

export const getLinearLabel = (element: any) => {
  if (element?.meta?.name) {
    return element.meta.name;
  }

  const legendKey = normalizeKind(element?.legendKey);
  const type = normalizeKind(element?.type);

  if (
    [
      'potentialjobprotection',
      'approvedjpcontinuous',
      'pendingjpcontinuous',
      'deniedjpcontinuous',
    ].includes(legendKey) ||
    type === 'jobprotection'
  ) {
    return 'Job Protection';
  }

  if (
    legendKey === 'potentialpaidbenefits' ||
    type === 'potentialpaidbenefits'
  ) {
    return 'Partial Earnings';
  }

  return 'Paid Benefits';
};

export const buildLinearBars = (rawElements: any[]) => {
  if (!Array.isArray(rawElements) || rawElements.length === 0) {
    return [] as LinearBar[];
  }

  const bars: LinearBar[] = [];
  rawElements.forEach((element) => {
    const styleType = toStyleType(element);
    if (!styleType) {
      return;
    }

    if (
      !dayjs(element?.startDate).isValid() ||
      !dayjs(element?.endDate).isValid()
    ) {
      return;
    }

    const entryId = `${element.legendKey}-${element.startDate}-${
      element.endDate
    }-${element?.meta?.name ?? ''}`;

    bars.push({
      id: entryId,
      label: getLinearLabel(element),
      startDate: element.startDate,
      endDate: element.endDate,
      styleType,
    });

    if (
      hasValue(element?.meta?.waitingPeriodStartDate) &&
      hasValue(element?.meta?.waitingPeriodEndDate) &&
      dayjs(element?.meta?.waitingPeriodStartDate).isValid() &&
      dayjs(element?.meta?.waitingPeriodEndDate).isValid()
    ) {
      bars.push({
        id: `wait-${element.startDate}-${element.endDate}-${
          element?.meta?.name ?? ''
        }`,
        label: 'Waiting Period',
        startDate: element.meta.waitingPeriodStartDate,
        endDate: element.meta.waitingPeriodEndDate,
        styleType: 'wait',
        relatedToId: entryId,
      });
    }
  });

  const dedupe = new Map<string, LinearBar>();
  bars.forEach((bar) => {
    if (!dedupe.has(bar.id)) {
      dedupe.set(bar.id, bar);
    }
  });

  const dedupedBars = [...dedupe.values()];
  const relatedWaitBars = dedupedBars.filter(
    (bar) => bar.styleType === 'wait' && !!bar.relatedToId,
  );
  const standaloneWaitBars = dedupedBars
    .filter((bar) => bar.styleType === 'wait' && !bar.relatedToId)
    .sort(barsByDateSort);
  const parentBars = dedupedBars
    .filter((bar) => !(bar.styleType === 'wait' && !!bar.relatedToId))
    .sort((a, b) => {
      const typeDiff =
        barStyleSortOrder[a.styleType] - barStyleSortOrder[b.styleType];
      if (typeDiff !== 0) {
        return typeDiff;
      }

      return barsByDateSort(a, b);
    });

  const waitingByParentId = new Map<string, LinearBar[]>();
  relatedWaitBars.forEach((bar) => {
    const relatedToId = bar.relatedToId;
    if (!relatedToId) {
      return;
    }

    const group = waitingByParentId.get(relatedToId) ?? [];
    group.push(bar);
    waitingByParentId.set(relatedToId, group);
  });

  const orderedBars: LinearBar[] = [...standaloneWaitBars];
  parentBars.forEach((bar) => {
    const relatedWaitGroup = waitingByParentId.get(bar.id);
    if (relatedWaitGroup?.length) {
      orderedBars.push(...relatedWaitGroup.sort(barsByDateSort));
    }
    orderedBars.push(bar);
  });

  return orderedBars;
};

export const findMilestoneDate = (rawElements: any[], names: string[]) => {
  const match = rawElements.find(
    (el) =>
      (normalizeKind(el?.legendKey) === 'milestone' ||
        normalizeKind(el?.type) === 'milestone') &&
      names.some((name) =>
        `${el?.meta?.name ?? ''}`.toLowerCase().includes(name.toLowerCase()),
      ),
  );
  return match?.startDate;
};

export const getTimelineBounds = (bars: LinearBar[]) => {
  if (bars.length === 0) {
    return {
      start: null as dayjs.Dayjs | null,
      end: null as dayjs.Dayjs | null,
    };
  }

  let start = dayjs(bars[0].startDate);
  let end = dayjs(bars[0].endDate);

  bars.forEach((bar) => {
    const barStart = dayjs(bar.startDate);
    const barEnd = dayjs(bar.endDate);
    if (barStart.isBefore(start)) {
      start = barStart;
    }
    if (barEnd.isAfter(end)) {
      end = barEnd;
    }
  });

  return { start, end };
};

export const getRangeBounds = (
  ranges: { startDate?: string; endDate?: string }[],
) => {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return {
      start: null as dayjs.Dayjs | null,
      end: null as dayjs.Dayjs | null,
    };
  }

  const validRanges = ranges.filter(
    (range) =>
      dayjs(range?.startDate).isValid() && dayjs(range?.endDate).isValid(),
  );

  if (validRanges.length === 0) {
    return {
      start: null as dayjs.Dayjs | null,
      end: null as dayjs.Dayjs | null,
    };
  }

  let start = dayjs(validRanges[0].startDate);
  let end = dayjs(validRanges[0].endDate);

  validRanges.forEach((range) => {
    const rangeStart = dayjs(range.startDate);
    const rangeEnd = dayjs(range.endDate);
    if (rangeStart.isBefore(start)) {
      start = rangeStart;
    }
    if (rangeEnd.isAfter(end)) {
      end = rangeEnd;
    }
  });

  return { start, end };
};

export const toValidDay = (value?: string) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

export const getDomainBounds = (
  dates: Array<dayjs.Dayjs | null | undefined>,
): { start: dayjs.Dayjs | null; end: dayjs.Dayjs | null } => {
  const validDates = dates.filter((d): d is dayjs.Dayjs => !!d && d.isValid());

  if (validDates.length === 0) {
    return { start: null, end: null };
  }

  let start = validDates[0];
  let end = validDates[0];

  validDates.forEach((date) => {
    if (date.isBefore(start)) {
      start = date;
    }
    if (date.isAfter(end)) {
      end = date;
    }
  });

  return { start, end };
};

export const getUnifiedTimelineBounds = ({
  rangeStart,
  rangeEnd,
  dueDate,
  leaveEnd,
}: {
  rangeStart: dayjs.Dayjs | null;
  rangeEnd: dayjs.Dayjs | null;
  dueDate?: string;
  leaveEnd?: string;
}) => {
  const candidateDates: dayjs.Dayjs[] = [];

  if (rangeStart?.isValid()) {
    candidateDates.push(rangeStart);
  }
  if (rangeEnd?.isValid()) {
    candidateDates.push(rangeEnd);
  }

  if (dueDate && dayjs(dueDate).isValid()) {
    candidateDates.push(dayjs(dueDate));
  }

  if (leaveEnd && dayjs(leaveEnd).isValid()) {
    candidateDates.push(dayjs(leaveEnd));
  }

  if (candidateDates.length === 0) {
    return {
      start: null as dayjs.Dayjs | null,
      end: null as dayjs.Dayjs | null,
    };
  }

  return {
    start: dayjs.min(candidateDates),
    end: dayjs.max(candidateDates),
  };
};

export const getDurationDays = (startDate: string, endDate: string) =>
  Math.max(1, dayjs(endDate).diff(dayjs(startDate), 'day') + 1);

export const getBarLetter = (index: number) => {
  let value = index;
  let letter = '';

  do {
    letter = String.fromCodePoint(65 + (value % 26)) + letter;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);

  return letter;
};

export const getPct = (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  value: dayjs.Dayjs,
) => {
  const span = Math.max(1, end.diff(start, 'day'));
  const pos = value.diff(start, 'day');
  const pct = (pos / span) * 100;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
};

export const handleLeavePlanInit = async ({
  draftClaim,
  setLoading,
  setRawElements,
  setPeriods,
  setBenefits,
}: IHandleLeavePlanInit) => {
  try {
    const mapped = mapDraftClaimToTimeline(draftClaim);
    setRawElements(mapped.rawElements);
    setPeriods(mapped.periods);
    setBenefits(mapped.benefits);
  } catch (error) {
    log.info({ message: 'Failed to initialize leave plan timeline:', error });
  } finally {
    setLoading(false);
  }
};
